import { ReactElement, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { heapEvent } from '@equipmentshare/heaper'
import {
	useAddFulfillmentOrdersToPickOrderMutation,
	usePickOrderCandidateFulfillmentOrdersQuery,
} from '../../../../graphql'
import { useShowError, useShowSuccess } from '../../../../hooks/useToast'

type Props = {
	pickOrderId: string,
	/** Any order already in the run — it speaks for the run's destination. */
	memberFulfillmentOrderId: string | null,
	disabled?: boolean,
}

/**
 * Folds more orders into a run already open.
 *
 * A picker at the shelf often finds another order going to the same place is
 * ready, and re-picking it as a separate run means a second trip and a second
 * carton to the same dock. Candidates come from inventory-api, which compares
 * the run's real destination key.
 */
export default function AddOrdersToPickOrderControl( {
	pickOrderId,
	memberFulfillmentOrderId,
	disabled,
}: Props ): ReactElement | null {
	const showSuccess = useShowSuccess()
	const showError = useShowError()
	const [ open, setOpen ] = useState( false )
	const [ selectedIds, setSelectedIds ] = useState<string[]>( [] )

	const { data, loading } = usePickOrderCandidateFulfillmentOrdersQuery( {
		variables: { fulfillmentOrderId: memberFulfillmentOrderId ?? '' },
		skip: !open || !memberFulfillmentOrderId,
		fetchPolicy: 'network-only',
	} )

	const [ addOrders, { loading: isAdding } ] = useAddFulfillmentOrdersToPickOrderMutation()

	if ( !memberFulfillmentOrderId ) {
		return null
	}

	const candidates = data?.pickOrderCandidateFulfillmentOrders ?? []

	const close = () => {
		setSelectedIds( [] )
		setOpen( false )
	}

	const toggle = ( id: string ) => {
		setSelectedIds( ( prev ) => (
			prev.includes( id ) ? prev.filter( ( existing ) => existing !== id ) : [ ...prev, id ]
		) )
	}

	const handleAdd = async() => {
		try {
			const result = await addOrders( {
				variables: { input: { pickOrderId, fulfillmentOrderIds: selectedIds } },
			} )
			const added = result.data?.addFulfillmentOrdersToPickOrder
			if ( !added?.success ) {
				showError( `addOrders-${pickOrderId}`, added?.message ?? 'Could not add those orders.' )
				return
			}
			heapEvent( 'Inventory - DC Admin - Pick Order - Add Orders - Success', {
				pick_order_id: pickOrderId,
				fulfillment_order_count: selectedIds.length,
			} )
			showSuccess(
				`addOrders-${pickOrderId}`,
				`${selectedIds.length} ${selectedIds.length === 1 ? 'order' : 'orders'} added to this pick order.`,
			)
			close()
		} catch ( err ) {
			showError( `addOrders-${pickOrderId}`, err instanceof Error ? err.message : 'Could not add those orders.' )
		}
	}

	return (
		<>
			<Button color="inherit" disabled={disabled} onClick={() => setOpen( true )}>
				Add orders
			</Button>
			<Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
				<DialogTitle>Add orders to this pick order</DialogTitle>
				<DialogContent>
					<Stack spacing={1.5} sx={{ pt: 1 }}>
						{loading && (
							<Stack direction="row" spacing={1} alignItems="center">
								<CircularProgress size={18} />
								<Typography variant="body2" color="text.secondary">Looking for other orders…</Typography>
							</Stack>
						)}

						{!loading && candidates.length === 0 && (
							<Alert severity="info">Nothing else at this DC is going to the same place right now.</Alert>
						)}

						{candidates.map( ( node ) => (
							<FormControlLabel
								key={node.id}
								sx={{ display: 'flex' }}
								control={
									<Checkbox checked={selectedIds.includes( node.id )} onChange={() => toggle( node.id )} />
								}
								label={
									<Box>
										<Typography variant="body1">FO-{node.orderNumber}</Typography>
										<Typography variant="body2" color="text.secondary">
											{node.lineItems.length} {node.lineItems.length === 1 ? 'line' : 'lines'}
										</Typography>
									</Box>
								}
							/>
						) )}
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button color="inherit" onClick={close} disabled={isAdding}>Cancel</Button>
					<Button
						variant="contained"
						onClick={handleAdd}
						disabled={selectedIds.length === 0 || isAdding}
						startIcon={isAdding ? <CircularProgress size={16} color="inherit" /> : undefined}
					>
						Add ({selectedIds.length})
					</Button>
				</DialogActions>
			</Dialog>
		</>
	)
}
